import {
  useCallback, useEffect, useRef, useState,
} from 'react'
import type {
  FormEvent,
} from 'react'
import {
  Loader2,
} from 'lucide-react'
import {
  billingService,
  getApiErrorMessage,
} from '../services/api'
import type {
  PurchaseCreditsResponse,
} from '../services/api'
import {
  CheckoutModal,
} from './CheckoutModal'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  formatCredits,
  parseCreditAmount,
} from '../utils/purchaseCredits'

// Says the thing the reader actually wants to know first after a payment
// fails to start, and which is true here: no session was created, so nothing
// was charged.
const CHECKOUT_FAILED_FALLBACK =
  "We couldn't start that payment, and nothing has been charged. Give it another go."

// How long to keep looking for credits after a payment. The webhook usually
// lands in a second or two, but it is Stripe's clock, not ours, so the card
// says the credits are on their way rather than showing a stale balance as if
// nothing happened. Bounded on purpose: a poll that never gives up is a page
// that never settles.
const SETTLE_POLL_INTERVAL_MS = 2_000
const SETTLE_POLL_ATTEMPTS = 6

// Buying prepaid credits for the purchases the assistant makes (`web_task`).
//
// Nothing here credits anything: the wallet moves only when Stripe reports the
// invoice paid, so closing the modal mid-payment changes nothing and a payment
// that goes through lands even if the user never returns to this page. That is
// also why the balance is re-fetched when the modal closes rather than
// optimistically incremented — the webhook is the source of truth, and showing
// credits that have not arrived is the one lie this card must not tell.
export function PurchaseCreditsSection(): JSX.Element | null {
  const [wallet, setWallet] = useState<PurchaseCreditsResponse | null>(null)
  const [amount, setAmount] = useState('')
  const [selected, setSelected] = useState<number | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [awaitingCredits, setAwaitingCredits] = useState(false)
  // Which settle poll is the live one. A poll runs for ~10s, which is long
  // enough for the card to unmount or for a second checkout to close under it;
  // both bump this, and a poll whose token is stale stops rather than writing
  // state nobody is waiting for.
  const pollToken = useRef(0)

  // Returns the wallet it read, so a caller polling for a payment to land can
  // compare balances without racing React state.
  //
  // Only a 403 hides the card — that is the flag being off for this account,
  // which is not an error but an answer. Any other failure (a dropped
  // connection, a 500) leaves whatever we already had on screen: taking a card
  // away because one request failed loses the balance AND the retry with it.
  const load = useCallback(async (): Promise<PurchaseCreditsResponse | null> => {
    try {
      const response = await billingService.getPurchaseCredits()
      setWallet(response)
      setSelected((current) => current ?? response.preset_amounts_cents[0] ?? null)
      return response
    } catch (err) {
      if ((err as { response?: { status?: number } })?.response?.status === 403) {
        setWallet(null)
      }
      return null
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => () => {
    pollToken.current += 1
  }, [])

  if (!wallet) {
    return null
  }

  const chosenCents = selected ?? parseCreditAmount(amount)

  const startCheckout = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting) return
    if (
      chosenCents === null
      || chosenCents < wallet.min_amount_cents
      || chosenCents > wallet.max_amount_cents
    ) {
      setError(
        `Choose an amount between ${formatCredits(wallet.min_amount_cents)} and `
        + `${formatCredits(wallet.max_amount_cents)}.`,
      )
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const { client_secret } = await billingService.createPurchaseCreditsCheckout(
        `${window.location.origin}/settings`,
        chosenCents,
      )
      trackEvent(OnboardingEvents.PURCHASE_CREDITS_CHECKOUT_STARTED, {
        amount_cents: chosenCents,
      })
      setClientSecret(client_secret ?? null)
    } catch (err) {
      setError(getApiErrorMessage(err, CHECKOUT_FAILED_FALLBACK))
    } finally {
      setSubmitting(false)
    }
  }

  // The modal closes when the payment finishes AND when the user gives up, and
  // the client cannot tell those apart — only the webhook knows, and it may not
  // have landed yet. So poll briefly for the balance to move, and say credits
  // are on their way while we look. Either way the card ends up showing what
  // the server says, never a number we assumed.
  const pollForCredits = async (before: number) => {
    pollToken.current += 1
    const token = pollToken.current
    for (let attempt = 0; attempt < SETTLE_POLL_ATTEMPTS; attempt += 1) {
      const fresh = await load()
      if (pollToken.current !== token) return
      if (fresh && fresh.available_cents > before) break
      // No sleep after the last attempt — it would leave a timer running past
      // the point where anything is still waiting on it.
      if (attempt === SETTLE_POLL_ATTEMPTS - 1) break
      await new Promise((resolve) => setTimeout(resolve, SETTLE_POLL_INTERVAL_MS))
      if (pollToken.current !== token) return
    }
    // Either the credits landed or we stopped looking. The note comes down
    // either way: it is never replaced by an error, because a slow webhook and
    // an abandoned checkout are indistinguishable from here and calling the
    // second one a failed payment would be a lie.
    setAwaitingCredits(false)
  }

  const closeCheckout = () => {
    trackEvent(OnboardingEvents.PURCHASE_CREDITS_CHECKOUT_CLOSED, {
      amount_cents: chosenCents,
    })
    setClientSecret(null)
    setAwaitingCredits(true)
    void pollForCredits(wallet.available_cents)
  }

  return (
    <div id="purchase-credits" className="bg-white rounded-2xl border border-gray-200 p-6">
      {/* text-base with no icon, matching the other cards in this block
          (Dedicated Phone Number beside it, Assistant autonomy and iPhone
          Action Button around it) rather than the text-lg + icon heading of
          the Billing grid this card used to live in. The balance keeps the
          heading's own scale — it is the other half of the title line. */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">Purchase credits</h3>
        <span className="text-base font-semibold text-gray-900">
          {formatCredits(wallet.available_cents)}
        </span>
      </div>
      <p className="text-sm text-gray-600">
        Your assistant pays for things out of this balance — a subscription it
        renews for you, a ticket, anything it buys on the web. It can only ever
        spend what's here, and it asks you before each purchase.
      </p>
      {awaitingCredits && (
        <p className="mt-2 text-sm text-gray-500">
          If you just paid, the credits land here in a moment — you can leave
          this page, nothing is lost.
        </p>
      )}
      {/* Only when there is something held. A permanent "$0 reserved" line is
          noise, and the number only means anything mid-purchase. */}
      {wallet.held_cents > 0 && (
        <p className="mt-2 text-sm text-gray-500">
          {formatCredits(wallet.held_cents)} is reserved for a purchase in
          progress, so it isn't part of the balance above.
        </p>
      )}

      <form onSubmit={startCheckout} className="mt-5">
        <div className="flex flex-wrap gap-2">
          {wallet.preset_amounts_cents.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => {
                setSelected(preset)
                setAmount('')
                setError(null)
              }}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                selected === preset
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {formatCredits(preset)}
            </button>
          ))}
          <label className="relative">
            <span className="sr-only">Another amount</span>
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
              $
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              placeholder="Other"
              onChange={(e) => {
                setAmount(e.target.value)
                // A typed amount and a selected preset are the same field with
                // two inputs, so typing has to clear the selection — otherwise
                // the highlighted tile and the box disagree about what the
                // button will charge.
                setSelected(null)
                setError(null)
              }}
              className="w-28 pl-6 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </label>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          Add credits
        </button>
      </form>

      {clientSecret && (
        <CheckoutModal clientSecret={clientSecret} onClose={closeCheckout} />
      )}
    </div>
  )
}
